import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Typography, Box } from '@material-ui/core';

import { SimpleExpansionPanel } from 'components';

import { currencyFormatter } from 'utils/prices/formatter';
import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import useStyles from './styles';

const INFO_LIST_FIELDS = [
  { title: 'Overview', field: 'overview' },
  { title:'Description', field: 'description'},
  { title: 'Prices' },
  { title: 'Check-in / Check-out', field: 'checkInTime' },
  { title: 'What to bring', field: 'whatToBring' },
  { title: 'Additional info', field: 'additionalInfo' },
  { title: 'Cancellation / Expiration', field: 'cancellation' },
  { title: 'Bed details', field: 'bedDetails' },
  { title: 'Included', field: 'included' },
  { title: 'Not included', field: 'notIncluded' }
];

const INFO_ARRAY_FIELDS = [
  'whatToBring',
  'bedDetails',
  'included',
  'notIncluded'
];

const BookInfoList = ({
  activity,
  isWithoutDescriptionForm,
  currency,
  defaultExpanded,
  firstDefaultExpanded,
  isOverview,
  isImage,
  titleTypographyVariant,
  panelDetailsClass
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();

  const selectedLanguage = i18n.language;
  const list = [];

  const descForm = activity.descriptionForm;
  const descFormNorwegian = activity['descriptionForm-Norwegian'];
  const generalForm = activity.generalForm;
  const generalFormNorwegian = activity['generalForm-Norwegian'];

  INFO_LIST_FIELDS.map(item => {
    const fieldName = item.field;
    let itemField =
      selectedLanguage === 'Norway' && activity[`${fieldName}No`]
        ? activity[`${fieldName}No`]
        : activity[fieldName];

    if (!itemField || !itemField.length) {
      itemField = activity[fieldName];
    }

    let fieldValue = isWithoutDescriptionForm
      ? itemField
      : getFormByCurrentLanguage(selectedLanguage, descForm, descFormNorwegian)[
          item.field
        ];

    if (!fieldValue || !fieldValue.length) {
      fieldValue = isWithoutDescriptionForm
        ? itemField
        : getFormByCurrentLanguage('English', descForm, descFormNorwegian)[
            item.field
          ];
    }

    if (item.title === 'Bed details') {
      fieldValue = isWithoutDescriptionForm
        ? itemField
        : getFormByCurrentLanguage(
            selectedLanguage,
            generalForm,
            generalFormNorwegian
          )[item.field];
      if (!fieldName.length) {
        fieldValue = isWithoutDescriptionForm
          ? itemField
          : getFormByCurrentLanguage(
              'English',
              generalForm,
              generalFormNorwegian
            )[item.field];
      }
    }

    if (item.title === 'Overview' && !isOverview) {
      return;
    }

    if (item.title === 'Prices') {
      fieldValue = _getPricesInfo(activity, currency);

      list.push({
        title: item.title,
        description: fieldValue
      });
    } else {
      if (fieldValue && fieldValue.length) {
        if (INFO_ARRAY_FIELDS.includes(item.field)) {
          fieldValue = fieldValue.join(', ');
        } else {
          fieldValue = fieldValue.trim();
          if (!fieldValue.length) return;
        }

        if (item.field === 'checkInTime') {
          const checkInTime = moment(fieldValue, 'HH:mm:ss').format('HH:mm');
          const checkoutValue = isWithoutDescriptionForm
            ? activity['checkOutTime']
            : activity.descriptionForm['checkOutTime'];
          const checkOutTime = moment(checkoutValue, 'HH:mm:ss').format(
            'HH:mm'
          );
          fieldValue = (
            <>
              <span className={classes.checkInTime}>
                {t('Check-in')}: {checkInTime}
              </span>
              <span>
                {t('Check-out')}: {checkOutTime}
              </span>
            </>
          );
        }

        fieldValue = <Typography variant="body1">{fieldValue}</Typography>;

        list.push({
          title: item.title,
          image: isImage ? generalForm.coverPhoto.image : null,
          description: fieldValue
        });
      }
    }
  });

  return (
    <SimpleExpansionPanel
      list={list}
      defaultExpanded={defaultExpanded}
      firstDefaultExpanded={firstDefaultExpanded}
      titleTypographyVariant={titleTypographyVariant}
      panelDetailsClass={panelDetailsClass}
    />
  );
};

const _getPricePerDay = (item, currency) => {
  const classes = useStyles();
  const value = currencyFormatter(item[currency]);
  return (
    <Typography variant="body1">
      {currency} <span className={classes.price}>{value}</span>
    </Typography>
  );
};

const _getPricesInfo = (activity, currency) => {
  const classes = useStyles();
  const [t] = useTranslation();

  const prices = activity[ activity.pricesType];
  
  const isAccommodation = !activity.scheduleType;

    // These are for future prices and other prices checks
  const pricesFixed = activity?.pricesFixedForm || {};
  const pricesCategory =
    activity?.pricesCategoryForm?.categories ??
    activity?.pricesEqualForm?.categories ??
    [];
  const pricesDaily = activity?.pricesDailyForm?.days || [];
  const pricesDailySchedule = activity?.pricesDailyScheduleForm?.daysArray || [];

  const hasOtherPrices = () => {
    const fixed = parseFloat(pricesFixed?.[currency]) > 0;
    const category = pricesCategory.some(cat => parseFloat(cat?.[currency]) > 0);
    const daily = pricesDaily.some(day => 
      (day.categories || []).some(cat => parseFloat(cat?.[currency]) > 0)
    );
    const dailySchedule = pricesDailySchedule.some(day => 
      (day.categories || []).some(cat => parseFloat(cat?.[currency]) > 0)
    );
    
    return fixed || category || daily || dailySchedule;
  };

  // Handle future prices form
  if (activity.futurePriceForm === 'activityFuturePricesForm') {
     const uniqueRates = new Map();
    
    activity.activityFuturePricesForm?.forEach((item) => {
      const planKey = item.ratePlanTitle;
      const participantKey = item.participant;
      const compositeKey = `${planKey}-${participantKey}`;
      
      if (!uniqueRates.has(compositeKey) && item.prices[currency]) {
        uniqueRates.set(compositeKey, {
          title: item.ratePlanTitle,
          startDate: item.startDate,
          endDate: item.endDate,
          participant: item.participant,
          price: item.prices[currency]
        });
      }
    });

    const groupedByPlan = {};
    uniqueRates.forEach((rate) => {
      if (!groupedByPlan[rate.title]) {
        groupedByPlan[rate.title] = {
          title: rate.title,
          startDate: rate.startDate,
          endDate: rate.endDate,
          participants: []
        };
      }
      groupedByPlan[rate.title].participants.push({
        participant: rate.participant,
        price: rate.price
      });
    });

    return (
      <div>
        {Object.values(groupedByPlan).map((plan, index) => (
          <Box key={index} mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              {plan.title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {plan.startDate} → {plan.endDate}
            </Typography>
            
            {plan.participants.map((participant, pIndex) => (
              <Typography variant="body1" key={pIndex}>
                {currency}{' '}
                <span className={classes.price}>
                  {currencyFormatter(participant.price)}
                </span>{' '}
                × {participant.participant === 'Universal' ? t('Person') : t(participant.participant)}
              </Typography>
            ))}
          </Box>
        ))}
        
        {hasOtherPrices() && (
          <Box mt={4}>
            <Typography variant="body1" className={classes.price} fontWeight="bold">
              Other Dates Prices
            </Typography>

          
            {(activity.pricesType === 'pricesDailyScheduleForm') && (
              <div>
                {pricesDailySchedule.map((day, i) => {
                  return (
                    <div key={`schedule-day-${i}`} className={classes.pricesDailyItem}>
                      <Typography variant="body1">
                        {day.days.map(
                          (dayName, idx) => `${idx ? ',' : ''} ${t(dayName)}`
                        )}
                      </Typography>
                      {day.categories.map((category, index) => {
                        const value = currencyFormatter(category[currency]);
                        return (
                          <Typography variant="body1" key={index}>
                            {currency} <span className={classes.price}>{value}</span>{' '}
                            ×{' '}
                            {category.participant === 'Universal'
                              ? t('Person')
                              : t(category.participant)}
                          </Typography>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
    
            {/* Show pricesDailyForm if it matches */}
            {(activity.pricesType === 'pricesDailyForm') && (
              pricesDaily.map((day, i) => {
                const categories = day.categories || [];
                const validCategories = categories.filter(
                  cat => parseFloat(cat?.[currency]) > 0
                );

                if (validCategories.length === 0) return null;

                return (
                  <Box key={`day-${day.id || i}`} mt={1}>
                    <Typography variant="subtitle2">{t(day.day)}</Typography>
                    {validCategories.map((cat, j) => (
                      <Typography key={`cat-${j}`}>
                        <span className={classes.price}>
                          {currency} {currencyFormatter(parseFloat(cat[currency]))} ×{' '}
                          {cat.participant === 'Universal' ? t('Person') : t(cat.participant)}
                        </span>
                      </Typography>
                    ))}
                  </Box>
                );
              })
            )}

           
            {(activity.pricesType === 'pricesEqualForm' || activity.pricesType === 'pricesCategoryForm') && (
              pricesCategory.map((item, index) => {
                const value = parseFloat(item?.[currency]);
                if (value <= 0) return null;

                return (
                  <Typography variant="body1" key={index}>
                    {currency}{' '}
                    <span className={classes.price}>
                      {currencyFormatter(value)}
                    </span>{' '}
                    ×{' '}
                    {item.participant === 'Universal'
                      ? t('Person')
                      : t(item.participant)}
                  </Typography>
                );
              })
            )}
          </Box>
        )}

       {(activity.pricesType === 'pricesFixedForm') && (
              <div>
                {activity.scheduleType === 'schedulePassForm' ? (
                  <Typography variant="body1">
                    {currency} <span className={classes.price}>
                      {currencyFormatter(prices[currency])}
                    </span>
                  </Typography>
                ) : (
                  prices?.days?.map((item, index) => (
                    <div key={index} className={classes.pricesDailyItem}>
                      <Typography variant="body1">
                        {item.days?.map(
                          (day, idx) => `${idx ? ',' : ''} ${t(day)}`
                        )}
                      </Typography>
                      <Typography variant="body1">
                        {currency}{' '}
                        <span className={classes.price}>
                          {currencyFormatter(item[currency])}
                        </span>
                      </Typography>
                    </div>
                  ))
                )}
              </div>
            )}

      </div>
    );
  }

  switch (activity.pricesType) {
    case 'pricesFixedForm': {
      if (activity.scheduleType === 'schedulePassForm' || isAccommodation) {
        const value = currencyFormatter(prices[currency]);
        return (
          <Typography variant="body1">
            {currency} <span className={classes.price}>{value}</span>
          </Typography>
        );
      } else {
        return (
          <div>
            {prices?.days?.map((item, index) => (
              <div key={index} className={classes.pricesDailyItem}>
                <Typography variant="body1">
                  {item.days.map(
                    (day, index) => `${index ? ',' : ''} ${t(day)}`
                  )}
                </Typography>
                <Typography variant="body1">
                  {currency}{' '}
                  <span className={classes.price}>
                    {currencyFormatter(item[currency])}
                  </span>
                </Typography>
              </div>
            ))}
          </div>
        );
      }
    }
    
    case 'pricesDailyForm': {
      return (
        <div>
          {prices?.days?.map((item, index) => {
            return (
              <div key={index} className={classes.pricesDailyItem}>
                <Typography variant="body1">{t(item.day)}</Typography>
                {item.categories &&
                  item.categories.map((category, index) => {
                    const value = currencyFormatter(category[currency]);
                    return (
                      <Typography variant="body1" key={index}>
                        {currency}{' '}
                        <span className={classes.price}>{value}</span> ×{' '}
                        {category.participant === 'Universal'
                          ? (category.participant = t('Person'))
                          : t(category.participant)}
                      </Typography>
                    );
                  })}
                {!item.categories && item.day && (
                  <div key={index}>{_getPricePerDay(item, currency)}</div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
  
    case 'pricesDailyScheduleForm': {
      return (
        <div>
          {prices?.daysArray?.map((item, index) => {
            return (
              <div key={index} className={classes.pricesDailyItem}>
                <Typography variant="body1">
                  {item.days.map(
                    (day, index) => `${index ? ',' : ''} ${t(day)}`
                  )}
                </Typography>
                {item.categories.map((category, index) => {
                  const value = currencyFormatter(category[currency]);
                  return (
                    <Typography variant="body1" key={index}>
                      {currency} <span className={classes.price}>{value}</span>{' '}
                      ×{' '}
                      {category.participant === 'Universal'
                        ? (category.participant = t('Person'))
                        : t(category.participant)}
                    </Typography>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
    }
    
    case 'pricesCategoryForm':
    case 'pricesEqualForm': {
      return (
        <div>
          {prices?.categories?.map((item, index) => {
            const value = currencyFormatter(item[currency]);
            return (
              <Typography variant="body1" key={index}>
                {currency} <span className={classes.price}>{value}</span> ×{' '}
                {item.participant === 'Universal'
                  ? (item.participant = t('Person'))
                  : t(item.participant)}
              </Typography>
            );
          })}
        </div>
      );
    }
case 'pricesWeeklyForm': {
  return (
    <div>
      {prices?.weekList?.map((item, index) => (
        <div key={index} className={classes.pricesWeeklyItem}>
          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
            {item.type === 'weekday' ? t('Weekday') : t('Weekend')}
          </Typography>
          <Typography variant="body1">
            {item.days.map((day, i) => `${i ? ', ' : ''}${t(day)}`)}
          </Typography>
          <Typography variant="body1">
            {currency}{' '}
            <span className={classes.price}>
              {currencyFormatter(item[currency])}
            </span>
          </Typography>
        </div>
      ))}
    </div>
  );
}


case 'pricesHourlyForm': {
  return (
    <div>
      {prices?.hourlyList?.map((item, index) => {
        const timeDisplay = item.isMaster 
          ? item.time 
          : `Hourly (${item.startTime?.format('HH:mm')})`;
        
        return (
          <div key={item.id || index} className={classes.pricesHourlyItem}>
            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
              {timeDisplay}
            </Typography>
            <Typography variant="body1">
              {item[currency] > 0 ? (
                <>
                  {currency}{' '}
                  <span className={classes.price}>
                    {currencyFormatter(item[currency])}
                  </span>
                </>
              ) : (
                <span style={{ color: '#999', fontWeight: '600' }}>
                  Not available
                </span>
              )}
            </Typography>
          </div>
        );
      })}
    </div>
  );
}

    case 'futurePricesForm': {
      return (
        <div>
          {prices?.map((item, index) => {
            const value = currencyFormatter(item.value);
            return (
              <Box key={index} mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {item.ratePlanTitle}
                </Typography>
                <Typography variant="body2">
                  {item.startDate} → {item.endDate}
                </Typography>

                {item.prices[currency] && (
                  <Typography variant="body1" className={classes.price}>
                    {currency} {currencyFormatter(item.prices[currency])}
                  </Typography>
                )}
              </Box>
            );
          })}
          <hr />
          {hasOtherPrices() && (
            <Box mt={4}>
              <Typography
                variant="body1"
                className={classes.price}
                fontWeight="bold"
              >
                Other Dates Prices
              </Typography>

              {isAccommodation &&
                pricesDaily.map((day, i) => {
                  const value = parseFloat(day?.[currency]);
                  if (value <= 0) return null;
                  return (
                    <Box key={`day-${i}`} mt={1}>
                      <Typography variant="subtitle2">{day.day}</Typography>
                      <Typography>
                        {currency} {currencyFormatter(value)}
                      </Typography>
                    </Box>
                  );
                })}


               {pricesCategory.map((item, index) => {
                const value = parseFloat(item?.[currency]);
                if (value <= 0) return null;

                return (
                  <Typography variant="body1" key={index}>
                    {currency}{' '}
                    <span className={classes.price}>
                      {currencyFormatter(value)}
                    </span>{' '}
                    ×{' '}
                    {item.participant === 'Universal'
                      ? t('Person')
                      : t(item.participant)}
                  </Typography>
                );
              })}
            </Box>
          )}
        </div>
      );
    }

  }
};

export default BookInfoList;
